import React, { useState } from 'react';
import FormContext from '../context/FormContext';
import { useFormValidation } from '../../../../Hooks/useFormValidation';

/**
 * Form - Context-aware form wrapper
 * @param {Object} props
 * @param {Object} props.schema - Zod schema for validation
 * @param {Object} props.defaultValues - Initial form values
 * @param {Function} props.onSubmit - Submit handler (receives validated data)
 * @param {string} props.className - CSS classes
 * @param {React.ReactNode} props.children - Form content
 */
export const Form = ({
    schema,
    defaultValues = {},
    onSubmit,
    className = '',
    children
}) => {
    const [formData, setFormData] = useState(defaultValues);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formError, setFormError] = useState('');
    const [submitAttempted, setSubmitAttempted] = useState(false);

    const { errors, validate, validateField, clearError } = useFormValidation(schema);

    const handleFieldChange = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            clearError(name);
        }
    };

    const handleFieldBlur = (name) => {
        // Only validate on blur if submit has been attempted
        if (submitAttempted) {
            validateField(name, formData[name]);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError('');
        setSubmitAttempted(true); // Mark that submit has been attempted

        const result = validate(formData);

        if (!result.success) {
            return;
        }

        setIsSubmitting(true);
        try {
            await onSubmit(result.data, { setFormError, setFormData });
        } catch (error) {
            console.error("Form submission error:", error);
            setFormError(error.message || "An unexpected error occurred");
        } finally {
            setIsSubmitting(false);
        }
    };

    const [fieldLabels, setFieldLabels] = useState({});

    const registerLabel = (name, label) => {
        if (!name || !label) return;
        setFieldLabels(prev => {
            // Only update if label is different to avoid infinite loops
            if (prev[name] === label) return prev;
            return { ...prev, [name]: label };
        });
    };

    const unregisterLabel = (name) => {
        if (!name) return;
        setFieldLabels(prev => {
            const newLabels = { ...prev };
            delete newLabels[name];
            return newLabels;
        });
    };

    const contextValue = {
        formData,
        errors,
        isSubmitting,
        handleFieldChange,
        handleFieldBlur,
        formError,
        submitAttempted,
        fieldLabels,
        registerLabel,
        unregisterLabel
    };

    return (
        <FormContext.Provider value={contextValue}>
            <form onSubmit={handleSubmit} className={className}>
                {children}
                {formError && (
                    <div className="text-red-500 text-sm mt-2">{formError}</div>
                )}
            </form>
        </FormContext.Provider>
    );
};

export default Form;
